import {
  LinkAnchor,
  NotificationContainer,
  Section,
  SectionContent,
  SectionTitle,
  smBumps,
  useModal,
  xlBumps,
  xxlBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'
import { Navigate, useNavigate } from 'react-router'

import {
  getCurrentQuote,
  getPolicyholder,
  isRenewalProposal,
  RenewalProposal,
} from '../../../domain'
import { ExcessData } from '../../../hooks/adjustments/policy'
import {
  tFormats,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { useUpButton } from '../../../infrastructure/routing'
import { getProduct, ManagementType, useConfig } from '../../../state/configuration'
import { toDateTime } from '../../../util/date'
import { AppPath } from '../../AppPath'
import { ExcessSection } from '../../organisms/excess'
import { ProductDocumentsSection } from '../../organisms/product'
import { RenewalPromotionBanner } from '../../organisms/promotion'
import {
  ProposalCoverSection,
  ProposalInformationSection,
  ProposalVoluntaryExcessModal,
} from '../../organisms/proposal'
import { WriskTrans } from '../../organisms/trans'
import { FullPage } from '../../templates'
import { useProduct } from '../ProductProvider'
import { ProposalAdjustmentPricing } from '../proposal/pricing'
import { ProposalPath } from '../proposal/ProposalPath'
import { useProposal } from '../proposal/ProposalProvider'
import { AdditionalProfileManagement } from './components/AdditionalProfileManagement'
import { QuoteManagement } from './components/QuoteManagement'
import { RenewalAddOnSection } from './components/RenewalAddOnSection'

const tKey = TKey('pages.renewal-quote')

interface ContentProps {
  proposal: RenewalProposal
}

const RenewalQuotePageContent: FunctionComponent<ContentProps> = ({ proposal }) => {
  const { t } = useWriskTranslation()

  const navigate = useNavigate()
  const modal = useModal()

  const upButton = useUpButton({
    url: AppPath.HOME,
    id: 'home',
  })

  const { product, scheme } = useProduct()
  const {
    excess,
    renewal: { sections },
  } = useConfig(getProduct(scheme.schemeCode))

  const quote = getCurrentQuote(proposal)
  const profile = getPolicyholder(quote)

  const onOptOut = useCallback(() => navigate(ProposalPath.OPT_OUT), [navigate])

  const onExcessChange = useCallback(
    (data: ExcessData) =>
      modal.show({
        content: <ProposalVoluntaryExcessModal data={data} onComplete={modal.hide} />,
      }),
    [modal],
  )

  const header = t(tKey('header'), { profile })
  const subheader = (
    <WriskTrans
      t={t}
      i18nKey={tKey('subheader')}
      values={{
        startAt: t(tFormats('datetime.medium'), {
          value: toDateTime(proposal.sourcePolicy.detail.expiredAt),
        }),
      }}
      components={{
        OptOutLink: <LinkAnchor onClick={onOptOut} data-testid='button-opt-out' />,
      }}
    />
  )

  const configuredSections = sections.map((it) => {
    const content = (() => {
      switch (it.type) {
        case ManagementType.ADDITIONAL_PROFILE:
          return <AdditionalProfileManagement config={it} quote={quote} />
        default:
          return <QuoteManagement config={it} quote={quote} />
      }
    })()

    return (
      <Section data-testid={`section-${it.id}`} key={it.id} width={1} mb={xxlBumps}>
        <SectionTitle>{t(tKey('sections', it.id))}</SectionTitle>
        <SectionContent>{content}</SectionContent>
      </Section>
    )
  })

  return (
    <FullPage
      pageId='renewal-quote'
      header={header}
      subheader={subheader}
      upButton={upButton}
    >
      <NotificationContainer mb={xlBumps}>
        <RenewalPromotionBanner />
      </NotificationContainer>

      <Section data-testid='section-your-policy' width={1} mb={xxlBumps}>
        <SectionTitle>{t(tKey('sections.updated-terms'))}</SectionTitle>
        <SectionContent>
          <ProposalAdjustmentPricing
            product={product}
            proposal={proposal}
            adjustedQuote={proposal.quote}
            originalQuote={proposal.sourcePolicy.quote}
            width={1}
            p={2}
            variant='raised'
            mb={smBumps}
          />
          <ProposalInformationSection proposal={proposal} />
        </SectionContent>
      </Section>

      {configuredSections}

      <ProposalCoverSection data-testid='section-propolsal-cover' mb={xxlBumps} />

      <RenewalAddOnSection data-testid='section-addons' quote={quote} mb={xxlBumps} />

      <ExcessSection
        data-testid='section-excess'
        config={excess}
        currentQuote={quote}
        previousQuote={proposal.sourcePolicy.quote}
        onChange={onExcessChange}
        mb={xxlBumps}
      />

      <ProductDocumentsSection
        data-testid='section-product-document'
        quote={quote}
        header={t(tKey('sections.your-documents'))}
      />
    </FullPage>
  )
}

export const RenewalQuotePage: FunctionComponent = () => {
  const { proposal } = useProposal()

  return isRenewalProposal(proposal) ? (
    <RenewalQuotePageContent proposal={proposal} />
  ) : (
    <Navigate to={AppPath.HOME} />
  )
}
